import { Order } from '../types/order'

// Where an order was PLACED — a different question from who DRIVES it (`delivered_by`).
// An own-delivery order can still come from Thuisbezorgd or Uber Eats (the per-platform
// `delivery.delivered_by.<platform>` server setting), and that customer follows the order
// in the platform's app, not on our tracking page. Shared by the order history browser
// and the delivery board, so a channel has one name and one colour on every screen.

export const PLATFORM_NAMES: Record<string, string> = {
  uber_eats: 'Uber Eats',
  thuisbezorgd: 'Thuisbezorgd',
}

export interface OrderSourceBadge {
  /** Full channel name — "Thuisbezorgd". */
  label: string
  /** Compact form for tight columns — "TB". */
  short: string
  /** Tailwind classes — full literals, JIT-safe. */
  classes: string
  /**
   * What the customer can see about this order, for a tooltip. Only webshop orders get
   * our tracking link: the API generates a tracking token solely in createWebshopOrder,
   * and its token lookup is restricted to order_source 'webshop' (OrderService,
   * verified 2026-09-10). Update this if the API extends tracking to other channels.
   */
  customerView: string
}

// POS / Webshop / Kiosk straight from order_source; HubRise orders split by platform slug,
// with a grey "External" fallback for an unrecognised channel (platform NULL).
export const orderSourceBadge = (
  order: Pick<Order, 'order_source' | 'platform'>
): OrderSourceBadge => {
  switch (order.order_source) {
    case 'POS':
      return {
        label: 'POS',
        short: 'POS',
        classes: 'bg-blue-50 text-blue-700',
        customerView: 'Placed at the counter — no tracking link.',
      }
    case 'WEBSHOP':
      return {
        label: 'Webshop',
        short: 'Web',
        classes: 'bg-violet-50 text-violet-700',
        customerView: 'Ordered on our webshop — the customer has our tracking link.',
      }
    case 'KIOSK':
      return {
        label: 'Kiosk',
        short: 'Kiosk',
        classes: 'bg-teal-50 text-teal-700',
        customerView: 'Placed at the kiosk — no tracking link.',
      }
    case 'HUBRISE':
      if (order.platform === 'uber_eats') {
        return {
          label: 'Uber Eats',
          short: 'UE',
          classes: 'bg-green-50 text-green-700',
          customerView: 'Ordered via Uber Eats — the customer follows it in the Uber Eats app, not on our tracking page.',
        }
      }
      if (order.platform === 'thuisbezorgd') {
        return {
          label: 'Thuisbezorgd',
          short: 'TB',
          classes: 'bg-orange-50 text-orange-700',
          customerView: 'Ordered via Thuisbezorgd — the customer follows it in the Thuisbezorgd app, not on our tracking page.',
        }
      }
      return {
        label: 'External',
        short: 'Ext.',
        classes: 'bg-gray-100 text-gray-600',
        customerView: 'Ordered via an external platform — no tracking link from us.',
      }
    default:
      return {
        label: order.order_source,
        short: order.order_source,
        classes: 'bg-gray-100 text-gray-600',
        customerView: '',
      }
  }
}
